import { memo, useEffect, useRef, useState } from 'react';

import { AnimatePresence, motion } from 'framer-motion';
import type { Transition } from 'framer-motion';
import { useTranslation } from 'react-i18next';

import jackpotTitle from '../../../../../assets/jackpot-title.png';
import fallbackThumbnail from '../../../../../assets/thumbnails/game_thumbnail_seven.png';
import { SocialCoin } from '../../../../../components/SocialCoin/SocialCoin';
import { SpriteAnimation } from '../../../../../components/SpriteAnimation/SpriteAnimation';
import type { LoadedSprite } from '../../../../../components/SpriteAnimation/types';
import { SweepCoin } from '../../../../../components/SweepCoin/SweepCoin';
import { useFullyInView } from '../../../../../hooks/useFullyInView';
import type { WinnerTileVM } from '../types';

import styles from './WinnerAnimatedTile.module.scss';

// 40 frames played over ~1.5 seconds.
const SPRITE_FRAME_INTERVAL = 1500 / 40;

// Shared swap transition. With AnimatePresence mode="wait" the JACKPOT exit
// (0.4s) and the nickname/thumb entrance (0.4s) run back-to-back → 0.8s total.
const SWAP_TRANSITION: Transition = { duration: 0.4, ease: [0.33, 1, 0.68, 1] };

interface WinnerAnimatedTileProps {
  tile: WinnerTileVM;
  sprite: LoadedSprite;
  viewportRef: React.RefObject<HTMLDivElement>;
}

export const WinnerAnimatedTile = memo(({ tile, sprite, viewportRef }: WinnerAnimatedTileProps) => {
  const { t } = useTranslation();
  const tileRef = useRef<HTMLDivElement>(null);
  const isSweep = tile.mode === 'SC';
  const fullyVisible = useFullyInView(tileRef, viewportRef);

  // Play the coin burst once — the first time the tile is fully on-screen.
  const [spritePhase, setSpritePhase] = useState<'idle' | 'playing' | 'done'>('idle');
  useEffect(() => {
    if (spritePhase === 'idle' && fullyVisible && sprite.ready) {
      setSpritePhase('playing');
    }
  }, [spritePhase, fullyVisible, sprite.ready]);

  return (
    <div
      ref={tileRef}
      className={`${styles.tile} ${isSweep ? styles.tileSc : styles.tileGc} ${spritePhase === 'done' ? styles.aligned : ''}`}
    >
      {spritePhase === 'playing' && (
        <SpriteAnimation
          isPlaying
          className={styles.sprite}
          frameInterval={SPRITE_FRAME_INTERVAL}
          initialSprite={sprite}
          sprite={sprite}
          onCycleComplete={() => setSpritePhase('done')}
        />
      )}
      {spritePhase === 'done' && (
        <motion.img
          alt="provider logo"
          animate={{ opacity: 1, x: 0 }}
          className={styles.thumb}
          draggable={false}
          initial={{ opacity: 0, x: -60 }}
          src={tile.gameImageUrl || fallbackThumbnail}
          transition={SWAP_TRANSITION}
        />
      )}
      <motion.div layout className={styles.info} transition={SWAP_TRANSITION}>
        <div className={styles.titleSlot}>
          <AnimatePresence initial={false} mode="wait">
            {spritePhase === 'done' ? (
              <motion.div
                key="nickname"
                animate={{ opacity: 1, y: 0 }}
                className={styles.swapLayer}
                initial={{ opacity: 0, y: -16 }}
                transition={SWAP_TRANSITION}
              >
                <span className={styles.nickname}>{tile.nickname}</span>
              </motion.div>
            ) : (
              <motion.div
                key="jackpot"
                className={styles.swapLayer}
                exit={{ opacity: 0, y: -60 }}
                transition={SWAP_TRANSITION}
              >
                <img
                  alt={t('winnersBanner.jackpot')}
                  className={styles.jackpotTitle}
                  draggable={false}
                  src={jackpotTitle}
                />
              </motion.div>
            )}
          </AnimatePresence>
        </div>
        <span className={styles.amountRow}>
          {isSweep ? (
            <SweepCoin alt="sweep coin" className={styles.coin} />
          ) : (
            <SocialCoin alt="social coin" className={styles.coin} />
          )}
          <span className={`${styles.amount} ${isSweep ? styles.amountSc : styles.amountCc}`}>
            {tile.amount.toLocaleString()}
          </span>
        </span>
      </motion.div>
    </div>
  );
});

WinnerAnimatedTile.displayName = 'WinnerAnimatedTile';
